import { DoctorStatus } from '@shared/types';
import { Spinner } from '@web/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@web/components/ui/table';

import { cn } from '@web/lib/shadcn';

/** Define your status color mapping to avoid dynamic Tailwind classes. */
const statusClassMap: Record<DoctorStatus, string> = {
  active: 'bg-green-100 text-green-600',
  inactive: 'bg-gray-100 text-gray-600',
  'on-leave': 'bg-yellow-100 text-yellow-600',
  retired: 'bg-red-100 text-red-600',
};

type DoctorsTableProps = {
  doctors:
    | {
        id: string;
        status: DoctorStatus;
        displayName: string;
        brand: {
          id: string;
          name: string;
        };
      }[]
    | undefined;
  /**
   * If provided, each row becomes clickable. Clicking the row calls this callback,
   * passing in the row’s DoctorData.
   */
  onDetails?: (doctor: { id: string }) => void;
};

/**
 * A specialized Table component for Doctors that includes
 * a clickable row (for details) if `onDetails` is provided.
 */
export function DoctorsTable({ doctors, onDetails }: DoctorsTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Clinic</TableHead>
          <TableHead>Status</TableHead>
          {/* We no longer need a dedicated 'Details' column,
              since the entire row is clickable. */}
        </TableRow>
      </TableHeader>

      <TableBody>
        {!doctors ? (
          <TableRow>
            <TableCell colSpan={3} className="text-center">
              <Spinner />
            </TableCell>
          </TableRow>
        ) : doctors.length > 0 ? (
          doctors.map((doctor) => (
            <TableRow
              key={doctor.id}
              /* 
             Pass the row-level onDetails callback:
             If provided, the row is clickable and calls `onDetails` with the doc.
            */
              onDetails={
                onDetails
                  ? () => {
                      onDetails(doctor);
                    }
                  : undefined
              }
              className="transition-colors duration-200 hover:bg-gray-50"
            >
              <TableCell>{doctor.displayName}</TableCell>

              <TableCell>{doctor.brand.name}</TableCell>

              <TableCell>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs',
                    statusClassMap[doctor.status],
                  )}
                >
                  {doctor.status}
                </span>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={3} className="text-center">
              No doctors match your filters
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
